import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from 'shared-lib';
import { useAuth } from 'shared-lib';
import { Location } from '../hooks/useLocationsQueries';

const STORAGE_KEY = 'selectedLocationId';

interface LocationContextType {
  currentLocation: Location | null;
  availableLocations: Location[];
  setCurrentLocation: (location: Location) => void;
  isLoading: boolean;
  error: Error | null;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);

  const { data: locations = [], isLoading, error } = useQuery<Location[], Error>({
    queryKey: ['available-locations', user?.uid],
    queryFn: async () => {
      if (!user) return [];
      
      // 1. Получаем все записи locationUsers где пользователь админ или владелец
      const locationUsersQuery = query(
        collection(db, 'locationUsers'),
        where('userId', '==', user.uid),
        where('role', '==', 'admin')
      );
      
      const locationUsersSnapshot = await getDocs(locationUsersQuery);
      const locationIds = locationUsersSnapshot.docs.map(doc => doc.data().locationId);
      
      if (locationIds.length === 0) {
        // 2. Если нет прав админа нигде, проверяем только владение
        const ownedLocationsQuery = query(
          collection(db, 'locations'),
          where('ownerUid', '==', user.uid)
        );
        const ownedLocationsSnapshot = await getDocs(ownedLocationsQuery);
        const ownedLocations = ownedLocationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Location));
        
        return ownedLocations;
      }
      
      // 3. Получаем локации по ID где мы админы
      const adminLocationsQuery = query(
        collection(db, 'locations'),
        where(documentId(), 'in', locationIds)
      );
      const adminLocationsSnapshot = await getDocs(adminLocationsQuery);
      const adminLocations = adminLocationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Location));
      
      // 4. Получаем локации где мы владельцы
      const ownedLocationsQuery = query(
        collection(db, 'locations'),
        where('ownerUid', '==', user.uid)
      );
      const ownedLocationsSnapshot = await getDocs(ownedLocationsQuery);
      const ownedLocations = ownedLocationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Location));
      
      // 5. Объединяем результаты, убирая дубликаты по ID
      const allLocations = [...adminLocations];
      ownedLocations.forEach(ownedLocation => {
        if (!allLocations.some(loc => loc.id === ownedLocation.id)) {
          allLocations.push(ownedLocation);
        }
      });
      
      return allLocations;
    },
    staleTime: 30000, // Данные считаются свежими 30 секунд
    gcTime: 5 * 60 * 1000, // Кэшируем на 5 минут
    enabled: !!user
  });

  // Загружаем сохраненную локацию при старте
  useEffect(() => {
    if (!currentLocation && locations && locations.length > 0) {
      const savedLocationId = localStorage.getItem(STORAGE_KEY);
      if (savedLocationId) {
        const savedLocation = locations.find((loc: Location) => loc.id === savedLocationId);
        if (savedLocation) {
          setCurrentLocation(savedLocation);
        }
      }
    }
  }, [locations, currentLocation]);

  const handleSetCurrentLocation = (location: Location) => {
    setCurrentLocation(location);
    localStorage.setItem(STORAGE_KEY, location.id);
  };

  return (
    <LocationContext.Provider 
      value={{ 
        currentLocation, 
        availableLocations: locations || [],
        setCurrentLocation: handleSetCurrentLocation,
        isLoading,
        error: error || null
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}; 